// src/pages/public/GiMarketplacePage.jsx
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { giMarketplaceApi } from '@/api/services'
import { Link } from 'react-router-dom'
import { MapPin, Award, Filter, ChevronRight } from 'lucide-react'

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`

const GI_INFO = {
  'Banarasi':     { region: 'Varanasi, UP',       heritage: 'Mughal-era silk weaving tradition, UNESCO heritage craft' },
  'Kanjivaram':   { region: 'Kanchipuram, TN',    heritage: 'Temple silk saris with zari work, 400+ year tradition' },
  'Chanderi':     { region: 'Chanderi, MP',        heritage: 'Sheer handloom fabric blending silk and cotton' },
  'Pochampally':  { region: 'Telangana',           heritage: 'Ikat double-weave technique, geometric patterns' },
  'Mysore Silk':  { region: 'Karnataka',           heritage: 'Pure mulberry silk with gold zari, state-owned KSIC' },
  'Sambalpuri':   { region: 'Odisha',              heritage: 'Traditional bandha (tie-dye) weaving on handloom' },
  'Phulkari':     { region: 'Punjab',              heritage: 'Floral embroidery on hand-spun cotton, folk art' },
  'Patola':       { region: 'Patan, Gujarat',      heritage: 'Double ikat silk, one of the most complex weaves globally' },
  'Gadwal':       { region: 'Telangana',           heritage: 'Cotton body with silk border, temple-inspired motifs' },
  'Madhubani':    { region: 'Bihar',               heritage: 'Ancient Mithila art painted on fabric, mythological themes' },
}

function ProductCard({ product }) {
  const img = product.images?.[0]?.url || '/images/placeholder.png'
  const slug = product.slug || product.id

  return (
    <Link to={`/fabrics/${slug}`}
      className="group bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-shadow">
      <div className="aspect-square overflow-hidden bg-gray-100 relative">
        <img src={img} alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        <span className="absolute top-2 left-2 bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
          <Award className="w-3 h-3" /> GI Certified
        </span>
      </div>
      <div className="p-3">
        <p className="text-xs text-green-700 font-medium mb-0.5">{product.gi_tag_name}</p>
        <h3 className="text-sm font-bold text-gray-900 truncate">{product.name}</h3>
        <p className="text-xs text-gray-500 truncate">{product.category?.name}</p>
        <p className="text-base font-bold text-gray-900 mt-1">{fmt(product.price)}<span className="text-xs text-gray-400 font-normal">/m</span></p>
      </div>
    </Link>
  )
}

function RegionCard({ tag, info, count, minPrice, maxPrice }) {
  return (
    <div className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
          <MapPin className="w-5 h-5 text-green-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-gray-900">{tag}</h3>
          <p className="text-xs text-gray-500">{info?.region || 'India'}</p>
          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{info?.heritage}</p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-500">{count} products</span>
            <span className="text-xs font-medium text-green-700">
              {fmt(minPrice)} – {fmt(maxPrice)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function GiMarketplacePage() {
  const [selectedTag, setSelectedTag] = useState('')
  const [sort, setSort] = useState('popular')

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['gi-stats'],
    queryFn: () => giMarketplaceApi.stats().then(r => r.data),
  })

  const { data: tagsData } = useQuery({
    queryKey: ['gi-tags'],
    queryFn: () => giMarketplaceApi.tags().then(r => r.data),
  })

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['gi-products', selectedTag, sort],
    queryFn: () => giMarketplaceApi.list({ gi_tag: selectedTag || undefined, sort, per_page: 24 }).then(r => r.data),
  })

  const stats = statsData?.data || {}
  const regions = stats.regions || []
  const featured = stats.featured || []
  const tags = tagsData?.data || []
  const products = productsData?.data || []

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-10">
      {/* Hero */}
      <div className="bg-gradient-to-r from-green-800 to-green-600 rounded-2xl p-8 md:p-12 text-white">
        <div className="flex items-center gap-2 mb-3">
          <Award className="w-8 h-8" />
          <span className="text-xs font-bold uppercase tracking-widest opacity-80">Geographical Indication</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">GI Tag Premium Marketplace</h1>
        <p className="text-green-100 max-w-2xl text-sm md:text-base">
          Discover authentic, heritage textiles certified with Geographical Indication tags.
          Each fabric carries centuries of artisan tradition and regional craftsmanship.
        </p>
        <div className="flex items-center gap-6 mt-6 text-sm">
          <div>
            <span className="text-2xl font-bold">{stats.total_products || 0}</span>
            <span className="ml-1 text-green-200">GI Products</span>
          </div>
          <div>
            <span className="text-2xl font-bold">{regions.length}</span>
            <span className="ml-1 text-green-200">Heritage Regions</span>
          </div>
        </div>
      </div>

      {/* Regions */}
      {regions.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Heritage Regions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {regions.map(r => (
              <button key={r.gi_tag_name} onClick={() => setSelectedTag(r.gi_tag_name)}
                className="text-left w-full">
                <RegionCard
                  tag={r.gi_tag_name}
                  info={GI_INFO[r.gi_tag_name]}
                  count={r.product_count}
                  minPrice={r.min_price}
                  maxPrice={r.max_price}
                />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Featured (only when no filter active) */}
      {!selectedTag && featured.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Featured GI Products</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {featured.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* All GI Products with filters */}
      <section>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {selectedTag ? `${selectedTag} Collection` : 'All GI-Tagged Fabrics'}
          </h2>
          <div className="flex items-center gap-2">
            {selectedTag && (
              <button onClick={() => setSelectedTag('')}
                className="px-3 py-1.5 text-xs font-medium bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition">
                Clear filter
              </button>
            )}
            <select value={selectedTag} onChange={e => setSelectedTag(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm">
              <option value="">All Regions</option>
              {tags.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={sort} onChange={e => setSort(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm">
              <option value="popular">Most Popular</option>
              <option value="newest">Newest</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
          </div>
        </div>

        {productsLoading ? (
          <div className="text-center py-12 text-gray-400">Loading products...</div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <Award className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-400">No GI-tagged products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* Info banner */}
      <section className="bg-green-50 border border-green-200 rounded-xl p-6">
        <h3 className="font-bold text-green-900 mb-2">What is a GI Tag?</h3>
        <p className="text-sm text-green-800 leading-relaxed">
          A Geographical Indication (GI) tag is an intellectual property right that identifies goods
          originating from a specific geographical area. It certifies that the product possesses unique
          qualities and reputation attributable to that origin. GI-tagged fabrics on Fabrinza are sourced
          directly from certified artisans and verified for authenticity.
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          {['Authenticity Verified', 'Direct from Artisans', 'Heritage Craftsmanship', 'Premium Quality'].map(tag => (
            <span key={tag} className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">{tag}</span>
          ))}
        </div>
      </section>
    </div>
  )
}
